import { CircularProgress, useTheme } from "@mui/material";
import * as React from "react";

import { RobotoButton } from "@/shared/components/RobotoButton";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import {
  WorkspaceAccessibilityLevel,
  WorkspaceDatasetAccessibility,
} from "@/shared/domain/workspaces";

import styles from "../ShareWorkspaceModal.module.css";

interface AccessibilityConfirmationViewProps {
  workspaceId: string;
  targetAccessibility: WorkspaceAccessibilityLevel;
  datasets: WorkspaceDatasetAccessibility[];
  currentWorkspaceAccessibility: WorkspaceAccessibilityLevel;
  isUpdating: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Formats a dataset for display, showing name if available or "Unnamed dataset".
 */
function formatDatasetName(dataset: WorkspaceDatasetAccessibility): string {
  const name = dataset.name ?? "Unnamed dataset";
  return `${name} (${dataset.dataset_id})`;
}

/**
 * Computes what will change and what won't change when updating accessibility.
 */
function computeChangeset(
  workspaceId: string,
  targetAccessibility: WorkspaceAccessibilityLevel,
  datasets: WorkspaceDatasetAccessibility[],
  currentWorkspaceAccessibility: WorkspaceAccessibilityLevel,
) {
  const willChange: string[] = [];
  const wontChange: string[] = [];

  // Check workspace
  const workspaceMessage = `This workspace's layout, panel settings, labels, etc. (${workspaceId})`;
  if (currentWorkspaceAccessibility !== targetAccessibility) {
    willChange.push(workspaceMessage);
  } else {
    wontChange.push(workspaceMessage);
  }

  // Check datasets
  for (const dataset of datasets) {
    const datasetName = formatDatasetName(dataset);
    if (dataset.accessibility !== targetAccessibility) {
      willChange.push(datasetName);
    } else {
      wontChange.push(datasetName);
    }
  }

  return { willChange, wontChange };
}

/**
 * Returns the title and change messages for the confirmation view
 * based on the target accessibility level.
 */
function getConfirmationMessages(
  targetAccessibility: WorkspaceAccessibilityLevel,
): {
  title: string;
  willChangeMessage: string;
  wontChangeMessage: string;
} {
  if (targetAccessibility === "public") {
    return {
      title: "Make workspace publicly accessible?",
      willChangeMessage:
        "The following will be made viewable (read-only) to anyone with a Roboto account:",
      wontChangeMessage: "Already public (no change):",
    };
  }

  return {
    title: "Restrict workspace access?",
    willChangeMessage:
      "The following will have access restricted to your organization:",
    wontChangeMessage: "Already private (no change):",
  };
}

/**
 * Confirmation view that shows what will change when updating workspace accessibility.
 * Replaces the main modal content when a user selects a new accessibility level.
 */
export const AccessibilityConfirmationView: React.FC<
  AccessibilityConfirmationViewProps
> = ({
  workspaceId,
  targetAccessibility,
  datasets,
  currentWorkspaceAccessibility,
  isUpdating,
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme();

  const { willChange, wontChange } = React.useMemo(
    () =>
      computeChangeset(
        workspaceId,
        targetAccessibility,
        datasets,
        currentWorkspaceAccessibility,
      ),
    [workspaceId, targetAccessibility, datasets, currentWorkspaceAccessibility],
  );

  const { title, willChangeMessage, wontChangeMessage } =
    getConfirmationMessages(targetAccessibility);

  return (
    <div className={styles.confirmationView}>
      <RobotoTypography variant="h6" className={styles.confirmationTitle}>
        {title}
      </RobotoTypography>

      {willChange.length > 0 && (
        <div className={styles.changeSection}>
          <RobotoTypography
            variant="body2"
            className={styles.changeSectionLabel}
            sx={{ color: theme.palette.text.secondary }}
          >
            {willChangeMessage}
          </RobotoTypography>
          <ul className={styles.changeList}>
            {willChange.map((item, index) => (
              <li key={index}>
                <RobotoTypography variant="body2">{item}</RobotoTypography>
              </li>
            ))}
          </ul>
        </div>
      )}

      {wontChange.length > 0 && (
        <div className={styles.changeSection}>
          <RobotoTypography
            variant="body2"
            className={styles.changeSectionLabel}
            sx={{ color: theme.palette.text.secondary }}
          >
            {wontChangeMessage}
          </RobotoTypography>
          <ul className={styles.changeList}>
            {wontChange.map((item, index) => (
              <li key={index}>
                <RobotoTypography
                  variant="body2"
                  sx={{ color: theme.palette.text.disabled }}
                >
                  {item}
                </RobotoTypography>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.confirmationButtons}>
        <RobotoButton
          eventName="AccessibilityConfirmCancelClicked"
          className={styles.confirmationButton}
          variant="outlined"
          onClick={onCancel}
          disabled={isUpdating}
        >
          Cancel
        </RobotoButton>
        <RobotoButton
          eventName="AccessibilityConfirmConfirmClicked"
          className={styles.confirmationButton}
          variant="contained"
          onClick={onConfirm}
          disabled={isUpdating}
          startIcon={isUpdating ? <CircularProgress size={16} /> : undefined}
        >
          {isUpdating ? "Updating..." : "Confirm"}
        </RobotoButton>
      </div>
    </div>
  );
};
